import { Space, Popconfirm, message, Skeleton, Table, Popover } from 'antd';
import styled from 'styled-components';
import type { ColumnsType } from 'antd/lib/table';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { PlusOutlined } from '@ant-design/icons';

import { MEASUREMENT_UNITS } from 'events/types';
import {
  getMeasurementUnits,
  deleteMeasurementUnits,
} from 'services/collections';
import MeasurementUnitsForm from 'pages/Dialogs/MeasurementUnitsForm';

import useTranslation from '../../customHooks/translate';

const MeasurementUnits = () => {
  const translation = useTranslation();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [measurementUnitUpdate, setMeasurementUnitUpdate] = useState(null);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);



  const columns: ColumnsType<any> = [
    {
      title: <span>{translation.paymentMethods.Title}</span>,
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: 300,
    },
    {
      title: <span>{translation.paymentMethods.Action}</span>,
      key: 'actions',
      align: 'center',
      width: 200,
      render: (_, record) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <EditDeleteIcon
                  src="assets/edit-icon.svg"
                  alt="Edit"
                  onClick={() => {
                    setMeasurementUnitUpdate(record);
                    setAddModalOpen(true);
                  }}
                />
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleDelete(record)}
                  okText="Yes"
                  placement="left"
                  cancelText="No"
                >
                  <EditDeleteIcon
                    src="assets/delete-icon.svg"
                    alt="Delete"
                    // onClick={() => handleDelete(record)}
                  />
                </Popconfirm>
              </div>
            }
          >
            {record.isEditable && (
              <MoreVertIcon
                style={{
                  fontSize: '29px',
                  border: '1px solid #292d32bf',
                  borderRadius: '50%',
                  padding: '4px',
                }}
              />
            )}
          </Popover>
        ) : (
          <Space size="middle">
            {record.isEditable && (
              <>
                <div style={{ cursor: 'pointer' }}>
                  <img
                    className="edit"
                    src="assets/edit-icon.svg"
                    alt="Edit"
                    onClick={() => {
                      setMeasurementUnitUpdate(record);
                      setAddModalOpen(true);
                    }}
                  />
                </div>
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleDelete(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <div style={{ cursor: 'pointer' }}>
                    <img
                      className="delete"
                      src="assets/delete-icon.svg"
                      alt="Delete"
                    />
                  </div>
                </Popconfirm>
              </>
            )}
          </Space>
        ),
    },
  ];

  const { status, data, isLoading, refetch, isFetching } = useQuery<
    any,
    AxiosError,
    any[]
  >({
    queryKey: [MEASUREMENT_UNITS],
    queryFn: getMeasurementUnits,
    select: (response) => response.data.data.map((d) => ({ ...d, key: d.id })),
    keepPreviousData: true,
    refetchOnMount: 'always',
  });
  const handleDelete = async (id) => {
    try {
      await deleteMeasurementUnits(id);
      refetch();
      message.success(translation.messages.successDeleted);
    } catch {
      message.error('An error occurred while deleting the unit');
    }
  };

  const handleSuccess = (action) => {
    refetch();
    message.success(
      action === 'added'
        ? translation.InvoiceLine.SuccessMessage
        : translation.messages.successUpdated
    );
  };

  return (
    <>
      <div className="mb-[20px] flex w-full flex-wrap items-center justify-between gap-[10px]">
        <p className="text-[20px] font-medium text-[#1D1D1D] md:text-[24px]">
          {translation.settingTab.Measurement}
        </p>
        <button
          onClick={() => setAddModalOpen(true)}
          className="flex w-auto w-full w-max items-center gap-[8px] rounded-[6px] rounded-[6px] bg-[#466afb] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-white md:px-[24px] md:py-[12px]"
        >
          <PlusOutlined />
          <p className="hidden md:block">
            {translation.MeasurementUnits.AddNewMeasurementButton}
          </p>
        </button>
      </div>

      <MeasurementUnitsForm
        open={addModalOpen}
        measurementUnitUpdate={measurementUnitUpdate}
        handleClose={() => {
          setMeasurementUnitUpdate(null);
          setAddModalOpen(false);
        }}
        onSuccess={handleSuccess}
      />
      <STableWrapper>
        {status === 'loading' ? (
          <Skeleton active />
        ) : (
          <Table
            sticky={{
              offsetHeader: 0,
            }}
            className="custom-bordered"
            bordered
            scroll={{ x: 'max-content' }}
            dataSource={data}
            columns={columns}
            loading={isLoading || isFetching}
            pagination={false}
            rowClassName={(_, i) =>
              i % 2 === 0 ? 'bg-[#466AFB08]' : 'bg-white'
            }
          />
        )}
      </STableWrapper>
    </>
  );
};

const STableWrapper = styled.div`
  margin-top: 24px;
  height: 500px;
  overflow: auto;
`;

const EditDeleteIcon = styled.img`
  cursor: pointer;
  width: 24px;
  height: 24px;
`;

export default MeasurementUnits;
